import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';
import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { useSearchParams } from 'react-router';
import {
  hotelGetChangeLogOptions,
  hotelQueryOptions,
} from '@quoruum/frontend-common/oas/gen/quoruum/@tanstack/react-query.gen';
import {
  changeLogQueryInfiniteOptions,
  hotelSettingGetChangeLogOptions,
  userQueryOptions,
  rateScrapeMetadataGetChangeLogOptions,
} from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type {
  ChangeEventBean,
  ChangeEntryBean,
  ChangeLogBean,
} from '@quoruum/frontend-common/oas/gen/quoruum/types.gen';
import type { UserBean } from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import { BOOKING_SITES } from '@quoruum/frontend-common/oas/gen/enumValues.gen';
import { Autocomplete } from '../components/Autocomplete';

const ENTITY_TYPE_OPTIONS = [
  { label: 'Hotel', value: 'Hotel' },
  { label: 'Rate Scrape Metadata', value: 'RateScrapeMetadata' },
  { label: 'Hotel Settings', value: 'HotelSettings' },
] as const;

type EntityTypeValue = (typeof ENTITY_TYPE_OPTIONS)[number]['value'];

const DEFAULT_BOOKING_SITE = 'BRAND';

type ChangeLogAuditRow = {
  event: ChangeEventBean;
  parentId?: string;
};

type EntityIdOption = {
  id: string;
  label: string;
};

const ENTITY_ID_SEARCH_DEBOUNCE_MS = 250;
const MIN_ENTITY_ID_SEARCH_LENGTH = 2;

function isJsonString(str: string | undefined): boolean {
  if (!str) return false;
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
}

function formatJsonForHighlight(str: string): string {
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
}

function formatTimestampTooltip(timestamp: string | undefined): string | undefined {
  if (!timestamp) {
    return undefined;
  }

  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'long',
  }).format(parsedDate);
}

function ValueCell({ value }: { value: string | undefined }): ReactElement {
  if (value !== undefined && isJsonString(value)) {
    return (
      <SyntaxHighlighter
        language="json"
        customStyle={{
          margin: 0,
          padding: '0.25rem 0.5rem',
          borderRadius: '0.25rem',
          fontSize: '0.7rem',
          lineHeight: 1.4,
          maxWidth: '420px',
        }}
        wrapLongLines
      >
        {formatJsonForHighlight(value)}
      </SyntaxHighlighter>
    );
  }
  return (
    <span className="font-mono text-xs break-all">
      {value ?? <span className="italic text-slate-400">null</span>}
    </span>
  );
}

function EntriesTable({ entries }: { entries: ChangeEntryBean[] | undefined }): ReactElement {
  if (!entries?.length) {
    return <span className="text-slate-400 text-xs italic">No entries</span>;
  }
  return (
    <table className="w-full text-xs border-collapse border border-slate-200">
      <thead>
        <tr className="bg-slate-100">
          <th className="border border-slate-200 px-2 py-1 text-left font-semibold text-slate-600 w-1/4">
            Field
          </th>
          <th className="border border-slate-200 px-2 py-1 text-left font-semibold text-slate-600 w-[37.5%]">
            Old Value
          </th>
          <th className="border border-slate-200 px-2 py-1 text-left font-semibold text-slate-600 w-[37.5%]">
            New Value
          </th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, idx) => (
          <tr key={idx} className="even:bg-slate-50">
            <td className="border border-slate-200 px-2 py-1 font-mono align-top">
              {entry.field ?? '—'}
            </td>
            <td className="border border-slate-200 px-2 py-1 align-top">
              <ValueCell value={entry.oldValue} />
            </td>
            <td className="border border-slate-200 px-2 py-1 align-top">
              <ValueCell value={entry.newValue} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ChangeLogRow({
  row,
  showParentId,
  userMap,
}: {
  row: ChangeLogAuditRow;
  showParentId: boolean;
  userMap: Map<string, UserBean>;
}): ReactElement {
  const audit = row.event.auditBean;
  const timestampTooltip = formatTimestampTooltip(audit?.timestamp);
  const modifiedByUser = audit?.modifiedBy ? userMap.get(audit.modifiedBy) : undefined;

  return (
    <tr className="border-t border-slate-200 align-top">
      {showParentId ? (
        <td className="w-48 px-4 py-3 align-top text-sm font-mono text-slate-800">
          {row.parentId ?? '—'}
        </td>
      ) : null}
      <td className="w-64 px-4 py-3 align-top text-sm text-slate-800">
        <div className="min-w-40 space-y-1">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Timestamp
            </span>
            <div className="text-xs text-slate-700" title={timestampTooltip}>
              {audit?.timestamp ?? '—'}
            </div>
          </div>
          <div>
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Modified By
            </span>
            <div className="text-xs text-slate-700">
              {modifiedByUser ? (
                <>
                  {modifiedByUser.name ?? '—'}{' '}
                  <span className="text-slate-400">({modifiedByUser.id})</span>
                </>
              ) : (
                (audit?.modifiedBy ?? '—')
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 w-full align-top">
        <EntriesTable entries={row.event.entries} />
      </td>
    </tr>
  );
}

export function ChangeLog(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();

  const appliedEntityType = (searchParams.get('entityType') ?? 'Hotel') as EntityTypeValue;
  const appliedEntityId = searchParams.get('entityId') ?? '';
  const appliedSite = searchParams.get('site') ?? DEFAULT_BOOKING_SITE;

  const [inputEntityType, setInputEntityType] = useState<EntityTypeValue>(appliedEntityType);
  const [inputEntityId, setInputEntityId] = useState(appliedEntityId);
  const [debouncedEntityIdSearch, setDebouncedEntityIdSearch] = useState(appliedEntityId);
  const [inputSite, setInputSite] = useState(appliedSite);

  useEffect(() => {
    setInputEntityType(appliedEntityType);
    setInputEntityId(appliedEntityId);
    setDebouncedEntityIdSearch(appliedEntityId);
    setInputSite(appliedSite);
  }, [appliedEntityType, appliedEntityId, appliedSite]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedEntityIdSearch(inputEntityId.trim());
    }, ENTITY_ID_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [inputEntityId]);

  const handleGo = (): void => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('entityType', inputEntityType);
    if (inputEntityId.trim()) {
      nextParams.set('entityId', inputEntityId.trim());
    } else {
      nextParams.delete('entityId');
    }
    if (inputEntityType === 'RateScrapeMetadata') {
      nextParams.set('site', inputSite);
    } else {
      nextParams.delete('site');
    }
    setSearchParams(nextParams);
  };

  const { data: hotelSearchResponse, isFetching: isHotelSearchFetching } = useQuery({
    ...(debouncedEntityIdSearch.length >= MIN_ENTITY_ID_SEARCH_LENGTH
      ? hotelQueryOptions({
          query: {
            search: debouncedEntityIdSearch,
            pageSize: 8,
          },
        })
      : hotelQueryOptions()),
    enabled: debouncedEntityIdSearch.length >= MIN_ENTITY_ID_SEARCH_LENGTH,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  });

  const hotelEntityIdOptions = useMemo<EntityIdOption[]>(() => {
    const results = hotelSearchResponse?.results ?? [];
    return results
      .filter((hotel) => typeof hotel.id === 'string')
      .map((hotel) => {
        const hotelName = hotel.name ?? 'Unknown Hotel';
        const cityName = hotel.city?.name;
        return {
          id: hotel.id as string,
          label: `${hotelName}${cityName ? `, ${cityName}` : ''}`,
        };
      })
      .slice(0, 8);
  }, [hotelSearchResponse]);

  const entityIdOptions = hotelEntityIdOptions;
  const isEntityIdSearchLoading = isHotelSearchFetching;

  // No entity ID: query recent changes for the selected entity type
  const {
    data: queryData,
    isLoading: isQueryLoading,
    error: queryError,
  } = useInfiniteQuery({
    ...changeLogQueryInfiniteOptions({
      query: { name: appliedEntityType, pageSize: 10 },
    }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    enabled: !appliedEntityId,
  });

  // With entity ID — hotel specific
  const {
    data: hotelLogData,
    isLoading: isHotelLoading,
    error: hotelError,
  } = useQuery({
    ...hotelGetChangeLogOptions({
      path: { id: appliedEntityId || '_placeholder_' },
    }),
    enabled: Boolean(appliedEntityId) && appliedEntityType === 'Hotel',
  });

  // With entity ID — rate scrape metadata specific
  const rateScrapeMetadataChangeLogOptions = rateScrapeMetadataGetChangeLogOptions({
    path: { externalId: `${appliedSite}:${appliedEntityId}` },
  });

  const {
    data: rsmLogData,
    isLoading: isRsmLoading,
    error: rsmError,
  } = useQuery({
    ...rateScrapeMetadataChangeLogOptions,
    enabled: Boolean(appliedEntityId) && appliedEntityType === 'RateScrapeMetadata',
  });

  const {
    data: hotelSettingsLogData,
    isLoading: isHotelSettingsLoading,
    error: hotelSettingsError,
  } = useQuery({
    ...hotelSettingGetChangeLogOptions({
      path: { id: appliedEntityId || '_placeholder_' },
    }),
    enabled: Boolean(appliedEntityId) && appliedEntityType === 'HotelSettings',
  });

  const changeLogs = useMemo<ChangeLogBean[]>(() => {
    if (!appliedEntityId) {
      return queryData?.pages[0]?.results ?? [];
    }
    if (appliedEntityType === 'Hotel') {
      return hotelLogData ? [hotelLogData] : [];
    } else if (appliedEntityType === 'HotelSettings') {
      return hotelSettingsLogData ? [hotelSettingsLogData] : [];
    }
    return rsmLogData ? [rsmLogData] : [];
  }, [
    appliedEntityId,
    appliedEntityType,
    queryData,
    hotelLogData,
    rsmLogData,
    hotelSettingsLogData,
  ]);

  const rows = useMemo<ChangeLogAuditRow[]>(() => {
    return changeLogs.flatMap((log) =>
      (log.events ?? []).map((event) => ({
        event,
        parentId: log.parentId,
      }))
    );
  }, [changeLogs]);

  const modifiedByIds = useMemo<string[]>(() => {
    const ids = new Set<string>();
    for (const row of rows) {
      const id = row.event.auditBean?.modifiedBy;
      if (id && id !== 'ur0') ids.add(id);
    }
    return [...ids];
  }, [rows]);

  const { data: usersData } = useQuery({
    ...userQueryOptions({ query: { ids: modifiedByIds } }),
    enabled: modifiedByIds.length > 0,
  });

  const userMap = useMemo<Map<string, UserBean>>(() => {
    const map = new Map<string, UserBean>();
    for (const user of usersData?.results ?? []) {
      if (user.id) map.set(user.id, user);
    }
    return map;
  }, [usersData]);

  const isLoading = appliedEntityId
    ? appliedEntityType === 'Hotel'
      ? isHotelLoading
      : appliedEntityType === 'HotelSettings'
        ? isHotelSettingsLoading
        : isRsmLoading
    : isQueryLoading;

  const error = appliedEntityId
    ? appliedEntityType === 'Hotel'
      ? hotelError
      : appliedEntityType === 'HotelSettings'
        ? hotelSettingsError
        : rsmError
    : queryError;

  const showParentId = !appliedEntityId;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h1 className="mb-6 text-2xl font-semibold tracking-tight text-slate-900">
        Changelog Viewer
      </h1>

      {/* Filter bar */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700" htmlFor="entity-type">
            Entity Type
          </label>
          <select
            id="entity-type"
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
            value={inputEntityType}
            onChange={(e) => {
              setInputEntityType(e.target.value as EntityTypeValue);
            }}
          >
            {ENTITY_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {inputEntityType === 'RateScrapeMetadata' ? (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="booking-site">
              Site
            </label>
            <select
              id="booking-site"
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
              value={inputSite}
              onChange={(e) => {
                setInputSite(e.target.value);
              }}
            >
              {BOOKING_SITES.map((site) => (
                <option key={site} value={site}>
                  {site}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="flex flex-col gap-1">
          <Autocomplete
            id="entity-id"
            label="Entity Id"
            value={inputEntityId}
            placeholder="Leave blank to query recent changes"
            options={entityIdOptions}
            minSearchLength={MIN_ENTITY_ID_SEARCH_LENGTH}
            searchLength={debouncedEntityIdSearch.length}
            isLoading={isEntityIdSearchLoading}
            loadingLabel="Searching entity ids"
            noResultsText="No matching entity ids."
            containerClassName="w-80"
            onValueChange={(value) => {
              setInputEntityId(value);
            }}
            onEscapeKey={() => {
              setInputEntityId('');
            }}
            onSelectOption={(option) => {
              setInputEntityId(option.id);
            }}
            getOptionKey={(option) => option.id}
            renderOption={({ option }) => (
              <>
                <span className="font-medium text-slate-900">{option.id}</span>
                <span className="ml-2 text-slate-500">{option.label}</span>
              </>
            )}
          />
        </div>

        <button
          type="button"
          onClick={handleGo}
          className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
        >
          Go
        </button>
      </div>

      {/* Results */}
      {isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {getApiErrorMessage(error, 'An unexpected error occurred while loading changelog data.')}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm italic text-slate-400">No changelog data found.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {showParentId ? (
                  <th className="w-48 px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">
                    Parent Id
                  </th>
                ) : null}
                <th className="w-64 px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">
                  Audit
                </th>
                <th className="px-4 py-3 text-left font-semibold text-slate-700 w-full">Entries</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 10).map((row, idx) => (
                <ChangeLogRow
                  key={`${row.parentId ?? 'unknown'}-${row.event.auditBean?.timestamp ?? idx}-${idx}`}
                  row={row}
                  showParentId={showParentId}
                  userMap={userMap}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
